import { XMarkIcon, CheckIcon } from "@heroicons/react/24/outline"
import React, { useState } from "react"

import Typography from "../../components/shared/Typography"

// Import all layout images
const layoutImages = {
  grid: require("../../../assets/images/builder/v2/layout-images/grid.png"),
  "grid-hover": require("../../../assets/images/builder/v2/layout-images/grid-hover.png"),
  "grid-selected": require("../../../assets/images/builder/v2/layout-images/grid-selected.png"),
  "scattered-imgs": require("../../../assets/images/builder/v2/layout-images/scattered-imgs.png"),
  "scattered-imgs-hover": require("../../../assets/images/builder/v2/layout-images/scattered-imgs-hover.png"),
  "scattered-imgs-selected": require("../../../assets/images/builder/v2/layout-images/scattered-imgs-selected.png"),
  list: require("../../../assets/images/builder/v2/layout-images/list.png"),
  "list-hover": require("../../../assets/images/builder/v2/layout-images/list-hover.png"),
  "list-selected": require("../../../assets/images/builder/v2/layout-images/list-selected.png"),
  "img-r": require("../../../assets/images/builder/v2/layout-images/img-r.png"),
  "img-r-hover": require("../../../assets/images/builder/v2/layout-images/img-r-hover.png"),
  "img-r-selected": require("../../../assets/images/builder/v2/layout-images/img-r-selected.png"),
  "content-centered": require("../../../assets/images/builder/v2/layout-images/content-centered.png"),
  "content-centered-hover": require("../../../assets/images/builder/v2/layout-images/content-centered-hover.png"),
  "content-centered-selected": require("../../../assets/images/builder/v2/layout-images/content-centered-selected.png"),
  "img-l": require("../../../assets/images/builder/v2/layout-images/img-l.png"),
  "img-l-hover": require("../../../assets/images/builder/v2/layout-images/img-l-hover.png"),
  "img-l-selected": require("../../../assets/images/builder/v2/layout-images/img-l-selected.png"),
  "full-img": require("../../../assets/images/builder/v2/layout-images/full-img.png"),
  "full-img-hover": require("../../../assets/images/builder/v2/layout-images/full-img-hover.png"),
  "full-img-selected": require("../../../assets/images/builder/v2/layout-images/full-img-selected.png")
}

const SectionTypeCard = ({ sectionType, isSelected, onSelect }) => {
  const [isHovered, setIsHovered] = useState(false)
  // Map section types to their corresponding images
  const getSectionImage = (sectionType, isSelected, isHovered = false) => {
    // Map section types to image names based on the UI shown in the image
    const imageMap = {
      sub_hero: "img-r",
      featured_services: "grid", // Grid of service blocks
      gallery: "scattered-imgs", // Gallery of images
      reviews: "list", // List with stars
      content: "img-r", // Content with image on right
      quote: "content-centered", // Centered quote
      cta_content: "img-l", // Content with image on left
      cta_banner: "full-img", // Full width banner
      faq: "list", // List format for FAQs
      keywords: "grid" // Grid of keywords/features
    }

    const baseImageName = imageMap[sectionType.value] || "content-centered"

    // Determine which image to show based on state
    let imageName = baseImageName
    if (isSelected) {
      imageName = `${baseImageName}-selected`
    } else if (isHovered) {
      imageName = `${baseImageName}-hover`
    }

    return layoutImages[imageName] || layoutImages["content-centered"]
  }

  const sectionConfig = {
    sub_hero: {
      title: "Hero",
      description: "Add a hero section for non-home pages with image and CTA."
    },
    featured_services: {
      title: "Featured services",
      description: "Showcase your key services with pricing and booking options to attract clients."
    },
    gallery: {
      title: "Gallery",
      description:
        "Showcase your work, environment, or results with a selection of high-quality images that tell your story."
    },
    reviews: {
      title: "Reviews",
      description: "Build trust by displaying client testimonials and star ratings from satisfied customers."
    },
    content: {
      title: "Content",
      description: "Create informative sections with text, images, and optional call-to-action buttons."
    },
    quote: {
      title: "Quote",
      description: "Inspire visitors with meaningful quotes or testimonials in an elegant, centered layout."
    },
    cta_content: {
      title: "Content",
      description: "Drive action with content sections that include prominent call-to-action buttons."
    },
    cta_banner: {
      title: "Banner",
      description: "Create impactful full-width banners with compelling calls-to-action."
    },
    faq: {
      title: "FAQ",
      description: "Help visitors find answers to common questions with expandable FAQ sections."
    },
    keywords: {
      title: "Keywords",
      description: "Highlight key features, specialties, or benefits in an organized grid layout."
    }
  }

  const config = sectionConfig[sectionType.value] || {
    title: sectionType.label,
    description:
      "Showcase your work, environment, or results with a selection of high-quality images that tell your story."
  }

  return (
    <div
      className={`group border-b border-gray-200 p-6 transition-all ${
        isSelected ? "border-l-4 border-l-teal bg-teal/5 hover:bg-teal/10" : "hover:bg-gray-50"
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}>
      <div className="flex items-center justify-between">
        <div className="flex items-start gap-4">
          {/* Section type icon */}
          <div className="h-12 w-12 shrink-0">
            <img
              src={getSectionImage(sectionType, isSelected, isHovered)}
              alt={`${config.title} layout`}
              className="h-full w-full object-contain transition-all duration-200"
              onError={(e) => {
                // Fallback to a default image if the specific one doesn't exist
                e.target.src = layoutImages["content-centered"]
              }}
            />
          </div>
          <div className="flex-1">
            <Typography variant="h5" className="mb-1 font-semibold">
              {config.title}
            </Typography>
            <Typography variant="body" className="text-gray-600">
              {config.description}
            </Typography>
          </div>
        </div>
        <div className="ml-4">
          {isSelected ? (
            <button
              className="flex items-center text-teal transition-colors hover:text-teal-dark"
              onClick={(e) => {
                e.preventDefault()
                onSelect(sectionType)
              }}
              title="Click to deselect">
              <CheckIcon className="mr-1 h-4 w-4 text-green-500" />
              <span className="text-sm font-medium">Selected</span>
            </button>
          ) : (
            <button
              className="rounded-md bg-teal px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-teal/90"
              onClick={(e) => {
                e.preventDefault()
                onSelect(sectionType)
              }}>
              Select
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

const AddSectionScreen = ({ availableLayouts, onClose, onAddSections }) => {
  const [selectedSections, setSelectedSections] = useState([])

  const handleSelectSection = (sectionType) => {
    setSelectedSections((prev) => {
      const isAlreadySelected = prev.some((s) => s.value === sectionType.value)
      if (isAlreadySelected) {
        return prev.filter((s) => s.value !== sectionType.value)
      } else {
        return [...prev, sectionType]
      }
    })
  }

  const handleClearAll = () => {
    setSelectedSections([])
  }

  const handleAdd = () => {
    if (selectedSections.length > 0) {
      onAddSections(selectedSections)
      onClose()
    }
  }

  const isSelected = (sectionType) => selectedSections.some((s) => s.value === sectionType.value)

  // Filter out default page hero since it can't be manually added; allow sub_hero for custom pages
  const addableSections = availableLayouts.filter((layout) => layout.value !== "hero")

  return (
    <div className="flex h-full flex-col bg-white">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 p-6">
        <Typography variant="h4" className="text-xl font-semibold">
          Add section
        </Typography>
        <button onClick={onClose} className="rounded p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      {/* Selection Counter */}
      {selectedSections.length > 0 && (
        <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-6 py-3">
          <Typography variant="small" className="text-gray-600">
            {selectedSections.length} section{selectedSections.length !== 1 ? "s" : ""} selected
          </Typography>
          <button
            onClick={handleClearAll}
            className="text-sm text-teal underline transition-colors hover:text-teal-dark">
            Clear all
          </button>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {addableSections.map((sectionType) => (
          <SectionTypeCard
            key={sectionType.value}
            sectionType={sectionType}
            isSelected={isSelected(sectionType)}
            onSelect={handleSelectSection}
          />
        ))}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end space-x-3 border-t border-gray-light bg-white px-8 py-6">
        <button
          className="rounded-md border border-gray-300 bg-white px-6 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
          onClick={onClose}>
          Close
        </button>
        <button
          className="rounded-md bg-teal px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-teal/90 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={handleAdd}
          disabled={selectedSections.length === 0}>
          Add {selectedSections.length > 0 ? `(${selectedSections.length})` : ""}
        </button>
      </div>
    </div>
  )
}

export default AddSectionScreen
